import { z } from "zod";
import { MAX_AMOUNT_CENTS } from "./money";
import { ROLES } from "./auth";
import { parseComposition } from "./kits";
import { variantSchema, vatRateSchema } from "./schemas";

// ── Users ────────────────────────────────────────────────────────────────────

/** Mirrors Better Auth's minimum length configured in the API. */
export const PASSWORD_MIN_LENGTH = 12;
const password = z.string().min(PASSWORD_MIN_LENGTH, "errors.field.password").max(128, "errors.field.tooLong");

export const userCreateSchema = z.object({
  name: z.string().trim().min(1, "errors.field.required").max(120, "errors.field.tooLong"),
  email: z.email("errors.field.email").transform((v) => v.toLowerCase()),
  role: z.enum(ROLES),
  password,
});
export type UserCreateInput = z.input<typeof userCreateSchema>;
export type UserCreateData = z.output<typeof userCreateSchema>;

export const userUpdateSchema = z.object({
  name: z.string().trim().min(1, "errors.field.required").max(120, "errors.field.tooLong"),
  role: z.enum(ROLES),
});
export type UserUpdateInput = z.input<typeof userUpdateSchema>;

export const userPasswordSchema = z.object({ password });

/** Self-service password change (Mon compte): the current password proves it is the account owner. */
export const passwordChangeSchema = z
  .object({
    currentPassword: z.string().min(1, "errors.field.required"),
    newPassword: password,
    confirmPassword: z.string(),
    revokeOtherSessions: z.boolean().default(true),
  })
  .refine((v) => v.newPassword === v.confirmPassword, { path: ["confirmPassword"], message: "errors.field.passwordMismatch" })
  .refine((v) => v.newPassword !== v.currentPassword, { path: ["newPassword"], message: "errors.field.passwordUnchanged" });
export type PasswordChangeInput = z.input<typeof passwordChangeSchema>;
export const userBanSchema = z.object({ banned: z.boolean() });

// ── Imports ──────────────────────────────────────────────────────────────────

export const IMPORT_TARGETS = ["customers", "suppliers", "products", "documents"] as const;
export type ImportTarget = (typeof IMPORT_TARGETS)[number];

/** What to do with a row that matches an existing record (email/SIRET, name or SKU). */
export const IMPORT_DUPLICATE_MODES = ["skip", "update"] as const;
export type ImportDuplicateMode = (typeof IMPORT_DUPLICATE_MODES)[number];

/** Rows are validated one by one server side, so one bad line never blocks the rest of the file. */
export const importBatchSchema = z.object({
  duplicates: z.enum(IMPORT_DUPLICATE_MODES).default("skip"),
  /** Validates and matches duplicates without writing anything, for the preview step. */
  dryRun: z.boolean().default(false),
  /** `line` is the row number in the source file, so the report points back to it. */
  rows: z
    .array(z.object({ line: z.number().int().min(1), data: z.record(z.string(), z.unknown()) }))
    .min(1)
    .max(5000),
});
export type ImportBatchInput = z.input<typeof importBatchSchema>;

/**
 * One spreadsheet row = one sellable reference. A known SKU updates its variant (prices, cost,
 * thresholds) from the cells the file filled; an unknown SKU becomes a single-variant product, which
 * needs a title and a price (checked line by line by the import, since an update needs neither).
 */
export const productImportRowSchema = variantSchema
  .pick({ sku: true, ean: true, weightGrams: true, costCents: true, reorderPoint: true, targetStock: true })
  .extend({
    title: z.string().trim().min(1, "errors.field.required").max(200, "errors.field.tooLong").optional(),
    salePriceCents: variantSchema.shape.salePriceCents.optional(),
    /** Sale price including VAT (IABAKO and Shopify catalogs are priced so), converted with the row's VAT rate. */
    salePriceGrossCents: variantSchema.shape.salePriceCents.optional(),
    productType: z
      .string()
      .trim()
      .max(120, "errors.field.tooLong")
      .transform((v) => (v === "" ? null : v))
      .nullable()
      .optional(),
    vatRateBp: vatRateSchema.default(2000),
    /** Main supplier, matched by name (case-insensitive) against the supplier list. */
    supplierName: z
      .string()
      .trim()
      .max(200, "errors.field.tooLong")
      .transform((v) => (v === "" ? null : v))
      .nullable()
      .optional(),
    /** Opening stock: booked once, only while the article has no stock history yet. */
    initialStock: z.coerce.number().int("errors.field.number").min(0, "errors.field.positive").max(1_000_000).optional(),
    /** Reference the article takes from now on (migration of a catalogue): the row is still matched by `sku`. */
    newSku: z
      .string()
      .trim()
      .max(64, "errors.field.tooLong")
      .transform((v) => (v === "" ? null : v.toUpperCase()))
      .nullable()
      .optional(),
    /** Components of a kit, "REF-A + 2 x REF-B": replaces the composition of the variant. */
    composition: z
      .string()
      .trim()
      .max(2000, "errors.field.tooLong")
      .transform((v, ctx) => {
        if (v === "") return null;
        const parts = parseComposition(v);
        if (!parts) {
          ctx.addIssue({ code: "custom", message: "errors.field.composition" });
          return z.NEVER;
        }
        return parts;
      })
      .nullable()
      .optional(),
  })
  .transform(({ salePriceGrossCents, ...row }) => ({
    ...row,
    salePriceCents:
      row.salePriceCents ??
      (salePriceGrossCents === undefined ? undefined : Math.round((salePriceGrossCents * 10_000) / (10_000 + row.vatRateBp))),
  }));
export type ProductImportRow = z.output<typeof productImportRowSchema>;

export interface ImportReport {
  created: number;
  updated: number;
  skipped: number;
  /** Product rows that changed a reference (« Nouvelle référence ») or set a composition. */
  renamed: number;
  compositions: number;
  /** Lines matching a record already in the ERP. */
  duplicateLines: number[];
  errors: { line: number; fields: Record<string, string> }[];
}

export const LEGACY_DOCUMENT_KINDS = ["quote", "invoice", "credit_note"] as const;
export type LegacyDocumentKind = (typeof LEGACY_DOCUMENT_KINDS)[number];
/** Where an archived document comes from: the previous tool, or Shopify orders older than the API allows. */
export const LEGACY_SOURCES = ["iabako", "shopify"] as const;
export type LegacySource = (typeof LEGACY_SOURCES)[number];

const legacyText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, "errors.field.tooLong")
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional();

/**
 * One quote, invoice or credit note from the previous tool (IABAKO), kept as a read-only archive:
 * it never enters the ERP's numbering, stock or accounting exports.
 */
export const legacyDocumentImportRowSchema = z
  .object({
    kind: z.enum(LEGACY_DOCUMENT_KINDS).default("invoice"),
    number: z.string().trim().min(1, "errors.field.required").max(60, "errors.field.tooLong"),
    issueDate: z.iso.date("errors.field.date"),
    customerName: legacyText(200),
    customerEmail: legacyText(200),
    netCents: z.coerce
      .number()
      .int("errors.field.number")
      .min(-MAX_AMOUNT_CENTS, "errors.field.amountTooLarge")
      .max(MAX_AMOUNT_CENTS, "errors.field.amountTooLarge")
      .nullable()
      .optional(),
    vatCents: z.coerce
      .number()
      .int("errors.field.number")
      .min(-MAX_AMOUNT_CENTS, "errors.field.amountTooLarge")
      .max(MAX_AMOUNT_CENTS, "errors.field.amountTooLarge")
      .nullable()
      .optional(),
    grossCents: z.coerce
      .number()
      .int("errors.field.number")
      .min(-MAX_AMOUNT_CENTS, "errors.field.amountTooLarge")
      .max(MAX_AMOUNT_CENTS, "errors.field.amountTooLarge"),
    paidCents: z.coerce
      .number()
      .int("errors.field.number")
      .min(-MAX_AMOUNT_CENTS, "errors.field.amountTooLarge")
      .max(MAX_AMOUNT_CENTS, "errors.field.amountTooLarge")
      .nullable()
      .optional(),
    status: legacyText(60),
    notes: legacyText(2000),
    source: z.enum(LEGACY_SOURCES).default("iabako"),
  })
  // Net + VAT must make the total (to the cent): the reports read the net, the archive shows the total.
  .refine((d) => d.netCents == null || d.vatCents == null || Math.abs(d.netCents + d.vatCents - d.grossCents) <= 1, {
    message: "errors.field.totalMismatch",
    path: ["grossCents"],
  })
  // A credit note counts against revenue by its kind; some exports also write its amounts negative,
  // which would turn it back into revenue. Its amounts are kept positive.
  .transform((row) => {
    if (row.kind !== "credit_note" || row.grossCents >= 0) return row;
    const flip = (v: number | null | undefined) => (v == null ? v : -v);
    return {
      ...row,
      netCents: flip(row.netCents),
      vatCents: flip(row.vatCents),
      grossCents: -row.grossCents,
      paidCents: flip(row.paidCents),
    };
  });
export type LegacyDocumentImportRow = z.output<typeof legacyDocumentImportRowSchema>;

/** A browser exception reported to the API; long texts are cut rather than refused. */
const clipped = (max: number) => z.string().transform((v) => v.slice(0, max));
export const clientErrorSchema = z.object({
  message: clipped(2000),
  stack: clipped(8000).optional(),
  url: clipped(1000).optional(),
});
